// file: src/components/modals/DialogHistoryModal.jsx
import React, { useState, useEffect } from 'react';
import { History, MessageCircle, Trash2, X, AlertTriangle } from 'lucide-react';
import { listConversations } from '../../services/conversationServices';
import { createConversation } from '../../services/conversationServices';
import { getJsonLocalForage } from '../../utils/localForageUtils';
import { useChatContext } from '../../context/ChatContext';
import { useProjectContext } from '../../context/ProjectContext';
import styles from './DialogHistoryModal.module.css';

/** 
 * Format a relative time-ago string. 
 * E.g. 2h ago, 1d ago, 1w ago, 2mo ago 
 */
function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return ''; // invalid date => fallback

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 5) return `${diffWeeks}w ago`;
  return `${diffMonths}mo ago`;
}

export default function DialogHistoryModal({ onClose, onDialogChosen, currentDialogId }) {
  const [error, setError] = useState('');
  
  const { setActiveProjectId } = useProjectContext();
  const { 
    dialogs, 
    loading,
    loadDialog,
    refreshDialogList,
    removeDialogById
  } = useChatContext();

  useEffect(() => {
    // Refresh the dialog list when modal opens
    refreshDialogList(false);
  }, []);

  // When user selects a dialog => pick or create conversation
  async function handleSelect(dialog) {
    try {
      console.log('Dialog selected:', dialog.id, 'Current dialog:', currentDialogId);
      
      // If selecting the current dialog, just close the modal
      if (dialog.id === currentDialogId) {
        console.log('Same dialog, closing modal');
        onClose();
        return;
      }

      setError('');
      
      // IMMEDIATELY close the modal - loading happens in background with indicator
      onClose();

      // Load conversation in background (chat panel shows "Just a moment..." indicator)
      const convs = await listConversations(dialog.id);
      console.log('Found conversations:', convs);
      let chosenConv = convs && convs.length > 0 ? convs[0] : null;
      if (!chosenConv) {
        console.log('No conversation found, creating new one');
        chosenConv = await createConversation(dialog.id);
      }

      console.log('Chosen conversation:', chosenConv);
      
      // Get dialog details to find project ID
      const details = await getJsonLocalForage(`dialogDetails-${dialog.id}`);
      if (details && details.project_id) {
        // Set the active project
        setActiveProjectId(details.project_id);
      }
      
      // Load the dialog using the ChatContext's loadDialog function
      await loadDialog(dialog.id, chosenConv.id);
      
      // Also notify parent component if it needs to update its state
      onDialogChosen?.({
        dialogId: dialog.id,
        conversationId: chosenConv.id,
      });
    } catch (err) {
      console.error('Dialog selection error:', err);
      // Modal is already closed, so error won't show here
      // But ChatContext will handle showing error state if needed
    }
  }

  // When user deletes a dialog => remove from local state + server
  async function handleDeleteDialog(e, dialogId) {
    e.stopPropagation();
    try {
      setError('');
      await removeDialogById(dialogId);
      // The removeDialogById function handles updating the dialogs state in ChatContext
      // and switching to another dialog if the deleted one was active
    } catch (err) {
      console.error('Failed to delete dialog:', err);
      setError(`Failed to delete dialog: ${err.message || 'Unknown error'}`);
    }
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <History className="h-4 w-4" />
            </div>
            <h2 className={styles.headerTitle}>Recent Conversations</h2>
          </div>
          <p className={styles.headerDescription}>
            Select a conversation to continue or start a new one.
          </p>
          {error && (
            <div className={styles.errorMessage}>
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              <p className={styles.errorText}>{error}</p>
            </div>
          )}
        </div>

        <div className={styles.content}>
          {loading && dialogs.length === 0 ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Loading conversations...</p>
            </div>
          ) : dialogs.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <MessageCircle className="h-6 w-6" />
              </div>
              <p className={styles.emptyText}>No conversations yet</p>
              <p className={styles.emptySubtext}>Start chatting to see your conversation history</p>
            </div>
          ) : (
            <div className={styles.dialogList}>
              {dialogs.map((dialog) => {
                // We assume the date is in dialog.create_time
                const dateDisplay = formatTimeAgo(dialog.create_time);
                const isCurrentDialog = dialog.id === currentDialogId;

                return (
                  <div
                    key={dialog.id}
                    className={`${styles.dialogItem} ${isCurrentDialog ? styles.dialogItemActive : ''}`}
                    onClick={() => handleSelect(dialog)}
                  >
                    <div className={styles.dialogItemContent}>
                      <div className={`${styles.dialogIcon} ${isCurrentDialog ? styles.dialogIconActive : ''}`}>
                        <MessageCircle className="h-4 w-4" />
                      </div>
                      <div className={styles.dialogText}>
                        <p className={`${styles.dialogTitle} ${isCurrentDialog ? styles.dialogTitleActive : ''}`}>
                          {dialog.name || 'Untitled Conversation'}
                        </p>
                        <p className={styles.dialogDate}>
                          {dateDisplay}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleDeleteDialog(e, dialog.id)}
                      className={styles.deleteButton}
                      title="Delete conversation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeButton}
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </button>
        </div>
      </div>
    </div>
  );
}



